import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, IconButton } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormErrorListener } from '../../Utils/formErrorHook';
import { preventSubmitOnEnter } from '../../Utils/helperFns';
import { getProductionListingforDropdown } from '../../redux/dashboardSlice';
import { useAnalytics } from '../../hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../helpers/analyticsEvents';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { AddDepartment } from '../../Utils/yupvalidations';
import PageHeading from '../../Components/PageHaeding';
import SectionContainer from '../../Components/SectionContainer';
import InputField from '../../Components/Input';
import Info from '../../Components/Info';
import { ImageCropperInput } from '../../Components/ImageCropperInput';
import CustomButton from '../../Components/CustomButton';
import WarningDialog from '../../Components/WarningDialog';
import Textarea from '../../Components/Textarea';
import { createDepartment } from '../../redux/commonSlice';
import {
  getDepartmentDetails,
  updateDepartment,
} from '../../redux/ProductionSlice';
import { RootState, useAppSelector } from '../../redux/store';

export default function CreateDepartment() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [base64File, setBase64File] = useState('');
  const [Document, setDocument] = useState('');
  const [isContactOpen, setIsContactOpen] = React.useState(false);
  const [load, setLoad] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  const { departmentDetails } = useAppSelector(
    (state: RootState) => state.production,
  );

  // Modify your useEffect to populate the form when departmentDetails data is available
  useEffect(() => {
    getProductionListingforDropdown();
    if (state?.id) {
      getDepartmentDetails(state?.id);
    }
  }, []);

  useEffect(() => {
    // Only run this effect in edit mode (i.e., when editing an existing department)
    if (!state?.id) return;

    if (departmentDetails && Object.keys(departmentDetails).length > 0) {
      setValue('deptName', departmentDetails.deptName || '');
      setValue('description', departmentDetails.description || '');

      if (departmentDetails.deptImage) {
        setBase64File(departmentDetails.deptImage);
      }

      if (
        departmentDetails.documents &&
        departmentDetails.documents.length > 0
      ) {
        // Clear existing documents
        while (documentFields.length > 0) {
          removeDocuments(0);
        }

        departmentDetails.documents.forEach((doc: any) => {
          appendDocuments({
            docName: doc.docName || '',
            type: doc.type || '',
            url: doc.url || doc.documentUrl || '',
            deptId: doc.deptId || departmentDetails._id,
          });
        });
      }
    }
  }, [departmentDetails, state?.id]);

  const { loading } = useSelector((state: any) => state?.episode);
  const formRef = useRef<HTMLFormElement>(null);
  const location = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(AddDepartment),
    mode: 'all',
    defaultValues: {
      deptName: '',
      description: '',
      image: '',
      documents: [],
    },
  });
  const {
    fields: documentFields,
    append: appendDocuments,
    remove: removeDocuments,
  } = useFieldArray({
    control,
    name: 'documents',
  });
  const deptName = watch('deptName');
  const description = watch('description');

  const onSubmit = (data: any) => {
    setIsAdding(true);

    const sanitizedDocuments = documentFields.map(
      ({ deptId, id, ...rest }: any) => rest,
    );

    const cleanPayload: any = (obj: any) => {
      if (Array.isArray(obj)) {
        return obj
          .map(cleanPayload)
          .filter(item => item !== null && item !== undefined);
      }

      if (obj && typeof obj === 'object') {
        return Object.entries(obj).reduce((acc, [key, value]) => {
          const cleanedValue = cleanPayload(value);
          if (
            cleanedValue !== null &&
            cleanedValue !== undefined &&
            cleanedValue !== '' &&
            !(Array.isArray(cleanedValue) && cleanedValue.length === 0)
          ) {
            acc[key] = cleanedValue;
          }
          return acc;
        }, {} as any);
      }

      return obj;
    };

    const { image, ...filteredData } = data;

    // Create a base payload
    let payload = {
      ...filteredData,
      documents: sanitizedDocuments,
    };

    // Only add entityType and entityId for creation, not for updates
    if (!state?.id) {
      payload = {
        ...payload,
        entityType: state?.entityType ? state?.entityType : 'productions',
        entityId: state?.entityId,
      };
    }

    const cleanedPayload = cleanPayload(payload);

    const finalPayload = {
      ...cleanedPayload,
    };

    if (base64File) {
      finalPayload.deptImage = data?.image || '';
    }

    // Choose the appropriate API call based on whether we're editing or creating
    if (state?.id) {
      // If id exists, we're updating
      updateDepartment(
        state?.id,
        finalPayload,
        () => {
          setIsAdding(false);
          navigate(-1);
        },
        () => {
          setIsAdding(false); // Re-enable button on error
        },
      );
    } else {
      // Otherwise, we're creating
      createDepartment(
        finalPayload,
        () => {
          setIsAdding(false);
          track(ANALYTICS_EVENTS.DEPARTMENT_CREATED, {
            entity_type: state?.entityType,
            entity_id: state?.entityId,
          });
          navigate(-1);
        },
        () => {
          setIsAdding(false); // Re-enable button on error
        },
      );
    }
  };

  const [department, setDepartment] = React.useState<any>({});

  const handleAddDocument = (docDetails: {
    deptId: string;
    docName: string;
    type: string;
    url: string;
  }) => {
    appendDocuments(docDetails);
  };

  useEffect(() => {
    setValue('image', base64File);
  }, [base64File]);

  useFormErrorListener(formRef, errors);

  return (
    <>
      <div className="pageBody">
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={e => preventSubmitOnEnter(e)}
          role="presentation"
        >
          <PageHeading
            navigateUrl={-1}
            pageTitle={state?.name || departmentDetails?.deptName || ''}
            title={state?.id ? `Edit Department` : `Create Department`}
            pageName={state?.id ? `Edit Department` : `Create Department`}
          />
          <div className="formScroller lg-inputs">
            <div>
              <SectionContainer
                heading="Department Detail"
                children={
                  <div className="formContainer">
                    <Grid container spacing={2}>
                      <Grid item md={4} xs={12}>
                        <InputField
                          id="deptName"
                          name="deptName"
                          label="Department Name"
                          error={!!errors['deptName']}
                          helperText={errors['deptName']?.message}
                          placeholder="Enter Department Name"
                          control={control}
                          requiredField
                          inputProps={{ maxLength: 50 }} // <-- Limit to 50 characters
                        />
                      </Grid>
                      <Grid item md={12} xs={12} className="chat_count">
                        {/* <Textarea
                          id="description"
                          name="description"
                          placeholder="Description"
                          label="Description"
                          rows={5}
                          limit={500}
                          error={!!errors['description']}
                          helperText={errors['description']?.message}
                          requiredField
                          control={control}
                        />
                        {watch('description')?.length ? (
                          <div
                            className={`hint_text  ${
                              !!errors['description'] ? 'hint_has_error' : null
                            }`}
                          >
                            <span>{watch('description')?.length + '/500'}</span>
                          </div>
                        ) : null} */}
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Description"
                          label="Description"
                          disabled={false}
                          rows={5}
                          //  limit={500}
                          charLimit={1000}
                          // getValues={watch}
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          control={control}
                          // requiredField
                        />
                        {watch('description')?.length ? (
                          <div
                            className={`hint_text  ${
                              !!errors.description ? 'hint_has_error' : null
                            }`}
                          >
                            <span>
                              {watch('description')?.length + '/1000'}
                            </span>
                          </div>
                        ) : null}
                      </Grid>
                    </Grid>
                  </div>
                }
              />

              <SectionContainer
                heading="Attach Department Image (Optional)"
                children={
                  <div className="formContainer">
                    <Grid container spacing={2} mb={2.2}>
                      <Grid item md={12} xs={12}>
                        <Info
                          theme="light"
                          infoText="Upload this image in a 2:1 aspect ratio as it will be displayed on the department detail page as a feature graphic."
                        />
                      </Grid>
                    </Grid>

                    <div className="imageContainer">
                      {!base64File && (
                        <ImageCropperInput
                          setLoad={setLoad}
                          onlyImage={true}
                          setProgress={setUploadProgress}
                          setUrl={setBase64File}
                          loading={load}
                          ratio={2 / 1}
                          title="Add Image"
                        />
                      )}
                      {base64File && (
                        <div className="">
                          <div className="imageItem ">
                            <Box className="selectimg_video">
                              <IconButton
                                className="removeImage"
                                onClick={() => {
                                  setBase64File('');
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                              <figure className="selected">
                                <img src={base64File} alt={'more'} />
                              </figure>
                            </Box>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
              {!state?.id && (
                <SectionContainer
                  heading="Attach Document"
                  children={
                    <div className="formContainer">
                      <Grid container spacing={2} mb={2.2}>
                        <Grid item md={12} xs={12}></Grid>
                      </Grid>

                      <div className="imageContainer">
                        {documentFields.length < 20 ? (
                          <ImageCropperInput
                            setLoad={setLoad}
                            setProgress={setUploadProgress}
                            setUrl={setDocument}
                            loading={load}
                            ratio={2 / 1}
                            title="Add Document"
                            type="Document"
                            onlyDocs
                            //   descTxt="or drag and drop here"
                            setDepartment={setDepartment}
                            onAddDocument={handleAddDocument}
                            guideline={true}
                          />
                        ) : (
                          <p className="MuiTypography-body2 error-text">
                            Maximum of 20 documents can be uploaded.
                          </p>
                        )}
                      </div>

                      <div className="doc-cards mt-12">
                        <Grid container spacing={2}>
                          {documentFields.map((item: any, index: number) => {
                            const fileExtension = item?.documentUrl
                              ?.split('.')
                              .pop()
                              ?.toLowerCase();
                            const isImage = [
                              'jpg',
                              'jpeg',
                              'png',
                              'gif',
                              'bmp',
                              'webp',
                            ].includes(fileExtension || '');

                            const isPDF = fileExtension === 'pdf';
                            return (
                              <Grid item md={6} xs={12} key={index}>
                                <div className="doc-card">
                                  <span className="icon">
                                    <i
                                      className={
                                        isPDF
                                          ? 'pdf-icon'
                                          : isImage
                                          ? 'img-icon'
                                          : 'pdf-icon'
                                      }
                                    ></i>
                                  </span>
                                  <p className="MuiTypography-body2 fw600">
                                    {item.docName}
                                  </p>

                                  <button
                                    className="dlt-btn ml-auto"
                                    type="button"
                                    onClick={() => removeDocuments(index)}
                                  >
                                    <i className="delete-icon"></i>
                                  </button>
                                </div>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </div>
                    </div>
                  }
                />
              )}

              <div className="formButtons end">
                <CustomButton
                  onClick={() => {
                    setOpen(true);
                  }}
                  size="large"
                  variant="outlined"
                  text="Cancel"
                  width="auto"
                  type="button"
                  id="cancel"
                  showIcon={false}
                />

                <CustomButton
                  size="large"
                  variant="contained"
                  text={state?.id ? 'Update' : 'Save'}
                  width="auto"
                  type="submit"
                  disabled={!isValid || !watch('deptName') || isAdding}
                  id="drafted"
                  showIcon={false}
                />
              </div>
            </div>
          </div>
        </form>

        <WarningDialog
          open={open}
          title="Cancel Department?"
          description="Are you sure you want to cancel the department?"
          type="warning"
          handleClose={() => setOpen(false)}
          onClick={() => {
            setOpen(false);
            navigate(-1);
          }}
          cancelBtnText="Cancel"
          confirmBtnText="Yes, Sure"
          showIcon={false}
        />
      </div>
    </>
  );
}
